import { useAtomValue } from 'jotai'
import Box from '@mui/material/Box'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Button from '@mui/material/Button'
import DiscountOutlinedIcon from '@mui/icons-material/DiscountOutlined'
import { Link } from 'react-router-dom'
import { alpha } from '@mui/material/styles'


import { selectedMunicipalityIdAtom } from "@/state"
import LoadingBox from "@/components/loading-box"
import ErrorBox from "@/components/error-box"
import { DISCOUNT_TYPE_LABEL_MAP, HISTORY_RETURN_PARAM_KEY, ROUTE_SEGMENTS } from '@/constants/general'
import { convertFromCents, printPrice } from '@/helpers'
import { useYearlyFeeDiscounts } from '@/hooks/api/use-yearly-fee-discount'
import { DiscountType } from '@/types'

const MAX_ROWS = 3

const getDalsiLabel = (count: number) => {
    if (count === 1) return 'další'
    if (count >= 2 && count <= 4) return 'další'
    return 'dalších'
}

function YearlyFeeDiscountsOverviewList({
    year,
} : {
    year: number | string,
}) {

    const municipalityId = useAtomValue(selectedMunicipalityIdAtom)
    
    const { status, data, error: queryError } = useYearlyFeeDiscounts(municipalityId, year)

    if (status === 'pending') {
        return (
            <LoadingBox />
        )
    }

    if (queryError) {
        return <ErrorBox
            error={queryError}
            message={`Nepovedlo se načíst slevy obce s ID ${municipalityId}`}
        />
    }

    const filteredDiscounts = data.filter(discount => (discount.type !== DiscountType.householdCustom && discount.type !== DiscountType.householdMemberCustom))

    const visibleDiscounts = filteredDiscounts.slice(0, MAX_ROWS)
    const remainingCount = filteredDiscounts.length - visibleDiscounts.length

    return (
        <Box sx={(theme) => ({
            border: `1px solid ${theme.palette.state.resolved}`,
            backgroundColor: 'discountGreen.light',
            pt: 1,
            pb: 2,
            borderRadius: 2.5,
            position: 'relative',
            flexGrow: 1,
            overflow: 'hidden',
        })}>
            <Box
                sx={(theme) => ({
                position: 'absolute',
                top: -10,
                right: -30,
                color: alpha('#d2edbe', 0.15),
                zIndex: 0,
                pointerEvents: 'none',
                })}
            >
                <DiscountOutlinedIcon sx={{ fontSize: 200 }} />
            </Box>
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
                overflowX: 'auto'
            }}>
                {filteredDiscounts.length === 0 ?
                    <Box px={2.5} textAlign="center" pt={2}>
                        Obec nemá nastavené žádné slevy pro rok {year}
                    </Box>
                :
                    <Table
                        size="small"
                        aria-label="Seznam slev"
                        sx={{
                            minWidth: 300,
                        }}
                    >
                        <TableHead>
                            <TableRow sx={{
                                '& td, th': { borderColor: 'discountGreen.dark' },
                            }}>
                                <TableCell sx={{ paddingLeft: 3 }}>Sleva</TableCell>
                                <TableCell>Výše (Kč)</TableCell>
                                <TableCell>Podmínka</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {visibleDiscounts.map(row => (
                                <TableRow
                                    key={row.id}
                                    sx={{
                                        '& td, th': { borderColor: 'discountGreen.dark' },
                                        '&:last-child td, &:last-child th': { border: 0 },
                                    }}
                                >
                                    <TableCell component="th" scope="row" sx={{ paddingLeft: 3 }}>
                                        <Box>{row.name}</Box>
                                        <Box color="customGrey.secondaryText">
                                            {DISCOUNT_TYPE_LABEL_MAP[row.type]}
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        {row.set_amount_to_zero ? 'Kompletní osvobození' :printPrice(convertFromCents(row.amount_cents))}
                                    </TableCell>
                                    <TableCell>
                                        {row.age_from ? `od ${row.age_from} let` : ''}
                                        {row.age_to ? `do ${row.age_to} let` : ''}
                                    </TableCell>
                                </TableRow>
                            ))}
                            {remainingCount > 0 && (
                                <TableRow sx={{
                                        '& td, th': { borderColor: 'discountGreen.dark' },
                                    }}>
                                    <TableCell
                                        colSpan={3}
                                        sx={{
                                            textAlign: 'center',
                                            color: 'customGrey.secondaryText',
                                            fontStyle: 'italic',
                                            py: 1.5,
                                        }}
                                    >
                                        + {remainingCount} {getDalsiLabel(remainingCount)}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                }
            </Box>
            <Box sx={{
                px: 2.5,
                pt: 2,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
            }}>
                <Button
                    variant="outlined"
                    color="terciary"
                    startIcon={<DiscountOutlinedIcon />}
                    component={Link}
                    to={`${ROUTE_SEGMENTS.yearlyFeeCollectionDiscounts}?${HISTORY_RETURN_PARAM_KEY}=true`}
                    sx={{
                        backgroundColor: 'discountGreen.main',
                        borderColor: 'discountGreen.dark',
                    }}
                >
                    Správa slev {year}
                </Button>
            </Box>
        </Box>
    )
}

export default YearlyFeeDiscountsOverviewList
